import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext.jsx";
import { apiFetch } from "../lib/api.js";

const ThemeContext = createContext(null);
const STORAGE_KEY = "conquest-theme";

function getInitialTheme() {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" ? "light" : "dark";
  } catch (e) {
    return "dark";
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);
  const { user } = useAuth();
  const syncedUserIdRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      /* localStorage unavailable (e.g. private browsing); theme still applies for this session */
    }
  }, [theme]);

  // Once the account loads (after login or on refresh), apply the theme
  // saved against the account. localStorage above is only the
  // pre-hydration fallback used before this resolves, to avoid a flash.
  useEffect(() => {
    if (!user || !user.theme) return;
    if (syncedUserIdRef.current === user.id) return;
    syncedUserIdRef.current = user.id;
    if (user.theme === "light" || user.theme === "dark") {
      setTheme(user.theme);
    }
  }, [user]);

  const applyTheme = useCallback(
    (next) => {
      setTheme(next);
      if (user) {
        apiFetch("/auth/theme", { method: "PUT", body: { theme: next } }).catch(() => {
          /* best-effort; localStorage still holds the preference for this browser */
        });
      }
    },
    [user]
  );

  const toggleTheme = useCallback(() => {
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      if (user) {
        apiFetch("/auth/theme", { method: "PUT", body: { theme: next } }).catch(() => {
          /* best-effort; localStorage still holds the preference for this browser */
        });
      }
      return next;
    });
  }, [user]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: applyTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
